// /app/frontend/src/lib/errors.js
//
// Centralized API error → safe string normalizer.
//
// React render rule: NEVER pass an object/array into a JSX child slot
// (including <Toaster /> from sonner, which renders the toast body as a
// React child). FastAPI / Pydantic 422 responses return `detail` as an
// array of error objects, so `toast.error(err.response.data.detail)`
// crashes React with "Objects are not valid as a React child" and
// white-screens the entire app.
//
// This module is the single source of truth for converting an Axios
// rejected error into a human-readable string. Any caller that needs
// to surface an error to the user MUST go through `formatApiError()`
// (or read `err.safeMessage` which is attached by the axios interceptor
// — see /app/frontend/src/lib/api.js).

/**
 * Convert a raw value (string | array | object | undefined) that came
 * from an API response into a safe display string. Never returns
 * undefined; never returns a non-string.
 */
function _coerceDetail(detail, fallback) {
  if (detail == null) return fallback;

  // Plain string → return as-is, but strip the "Value error, " prefix that
  // Pydantic wraps custom validator messages with.
  if (typeof detail === "string") {
    return detail.replace(/^Value error,\s*/i, "").trim() || fallback;
  }

  // FastAPI/Pydantic 422 → detail is an array of { type, loc, msg, input, ctx, url }.
  if (Array.isArray(detail)) {
    if (detail.length === 0) return fallback;
    const first = detail[0];
    if (typeof first === "string") return _coerceDetail(first, fallback);
    if (first && typeof first === "object") {
      const msg = (typeof first.msg === "string" && first.msg)
        || (typeof first.message === "string" && first.message)
        || null;
      if (msg) return _coerceDetail(msg, fallback);
    }
    // Last resort: stringify the first entry so we never render the array
    // directly into JSX.
    try { return JSON.stringify(first); } catch (_) { return fallback; }
  }

  // Structured object response — common shapes:
  //   { code: "email_not_verified", message: "...", email: "..." }
  //   { detail: "..." }  (nested)
  if (typeof detail === "object") {
    if (typeof detail.message === "string" && detail.message) return detail.message;
    if (typeof detail.msg === "string" && detail.msg) return detail.msg;
    if (typeof detail.detail !== "undefined") return _coerceDetail(detail.detail, fallback);
    if (typeof detail.error === "string" && detail.error) return detail.error;
    // Last resort: stringify, so we never pass a raw object to React.
    try { return JSON.stringify(detail); } catch (_) { return fallback; }
  }

  // Numbers, booleans, etc.
  try { return String(detail); } catch (_) { return fallback; }
}

/**
 * Public API.
 *
 * @param {*} err     — anything (typically an axios rejection, sometimes a
 *                       raw value, sometimes undefined).
 * @param {string} fallback — copy to show when we can't extract a useful
 *                       message from the error.
 * @returns {string} — guaranteed string, safe to drop into JSX / sonner toast.
 */
export function formatApiError(err, fallback = "Something went wrong. Please try again.") {
  if (err == null) return fallback;
  if (typeof err === "string") return _coerceDetail(err, fallback);
  // If somebody already attached safeMessage upstream, trust it.
  if (typeof err.safeMessage === "string" && err.safeMessage) return err.safeMessage;
  const detail = err?.response?.data?.detail;
  if (typeof detail !== "undefined") return _coerceDetail(detail, fallback);
  // Network failure / CORS / offline — no response body.
  if (typeof err.message === "string" && err.message) return err.message;
  return fallback;
}

export default formatApiError;
